import { T, UI } from "../constants/tokens";

// ─── One source for what a modal looks like ──────────────────────────────────
// Seven dialogs in this app had been written independently, and every one of
// them had picked its own answer:
//
//   radius     0 · 6 · 9 · 10 · 12 · 16 · 18   — seven values for one object
//   surface    raised (5) · overlay (1) · page (1)
//   shadow     none, in all seven
//   padding    "8px 20px 16px" · "20px 20px 22px" · "24px 28px" · 16 · …
//
// A user who opens two dialogs in one session sees two different objects. The
// styles below are the single answer; a call site spreads them and adds only
// what is genuinely its own (width, scroll behaviour, internal layout).
//
// Not exported as a wrapper component on purpose: the seven differ a lot on the
// INSIDE — a bottom sheet with a drag handle, a full-screen reader on mobile, a
// header/body split with its own scroll region — and forcing those through one
// JSX shell would have been a rewrite with real regression risk for no visual
// gain. What had to be shared is the box, and that is what is shared.

// The dim behind every dialog. `zIndex` is per call site: the app stacks
// alerts above the news reader above the sidebar, and that ordering is real.
export function modalScrim(zIndex = 1000, { align = "center", padding = 16 } = {}) {
  return {
    position: "fixed",
    inset: 0,
    background: UI.scrim,
    zIndex,
    display: "flex",
    alignItems: align === "bottom" ? "flex-end" : "center",
    justifyContent: "center",
    padding,
  };
}

// The gutter a dialog keeps between its edge and its content. It matches the
// spec's card padding (22/24) rather than the eight different values that were
// in use. Dialogs that carry their own header/body split set the panel padding
// to 0 and apply this to each section, so the visible gutter stays the same.
export const MODAL_PAD = "22px 22px 24px";
export const MODAL_PAD_X = 22;

/**
 * The dialog panel itself.
 *
 *   variant "dialog"     — centred card. The default.
 *   variant "sheet"      — rises from the bottom edge on mobile; square feet,
 *                          rounded shoulders.
 *   variant "fullscreen" — occupies the viewport. No radius and no border,
 *                          because there is no surrounding page to sit on.
 */
export function modalPanel({ variant = "dialog", width = 560, pad = true } = {}) {
  const base = {
    background: UI.surface.overlay,
    boxShadow: UI.shadow.modal,
    display: "flex",
    flexDirection: "column",
    ...(pad ? { padding: MODAL_PAD } : null),
  };

  if (variant === "fullscreen") {
    return {
      ...base,
      position: "fixed",
      inset: 0,
      width: "100%",
      height: "100dvh",
      maxHeight: "100dvh",
      border: "none",
      borderRadius: 0,
      overflow: "hidden",
    };
  }

  if (variant === "sheet") {
    return {
      ...base,
      width: "100%",
      maxWidth: width,
      maxHeight: "88dvh",
      overflowY: "auto",
      border: `1px solid ${UI.line.hairline}`,
      borderBottom: "none",
      borderRadius: `${T.radius.xl}px ${T.radius.xl}px 0 0`,
    };
  }

  return {
    ...base,
    width: "100%",
    maxWidth: width,
    maxHeight: "88dvh",
    overflowY: "auto",
    border: `1px solid ${UI.line.hairline}`,
    borderRadius: T.radius.xl,
  };
}
